import { useI18n } from "../i18n/I18nProvider";
import { localizeError } from "../auth/localizeError";
import { useEffect, useState, type FormEvent } from "react";
import { Consents } from "./Consents";
import { LanguageSettings } from "./LanguageSettings";
import { Mfa } from "./Mfa";
import { auth } from "../auth/client";
import type { Api, Profile, Usage } from "../types";
export function Settings({
  api,
  profile,
  usage,
  logout,
  onHistoryChange,
  onClose,
}: {
  api: Api;
  profile: Profile;
  usage: Usage | null;
  logout: () => Promise<void>;
  onHistoryChange: (value: boolean) => void;
  onClose?: () => void;
}) {
  const { t, locale } = useI18n();
  const number = new Intl.NumberFormat(locale);
  const [notice, setNotice] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [job, setJob] = useState<any>(null),
    [confirmDelete, setConfirmDelete] = useState(false),
    [password, setPassword] = useState(""),
    [deletionFactor, setDeletionFactor] = useState(""),
    [deletionCode, setDeletionCode] = useState("");
  useEffect(() => {
    if (!job?.id || ["completed", "failed"].includes(job.status)) return;
    let alive = true;
    const timer = setTimeout(() => {
      api
        .request(`/jobs/${job.id}`)
        .then((next) => {
          if (alive) setJob(next);
        })
        .catch((e) => {
          if (alive) setError(localizeError(e, locale));
        });
    }, 2500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [api, job]);
  async function downloadData() {
    setBusy(true);
    setError("");
    try {
      const content = await api.download(`/media/${job.result.asset_id}/download`);
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = "my-data.json";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(localizeError(e, locale));
    } finally {
      setBusy(false);
    }
  }
  async function exportData() {
    setBusy(true);
    setError("");
    try {
      setJob(await api.request("/data-exports", { method: "POST" }));
      setNotice(t("导出请求已提交，你可以在这里查看进度。", "Export requested. You can check its progress here."));
    } catch (e) {
      setError(localizeError(e, locale));
    } finally {
      setBusy(false);
    }
  }
  async function deleteAccount(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (deletionFactor) {
        await auth.verifyMfa({ factor_id: deletionFactor, code: deletionCode });
      } else {
        const session = await auth.getSession();
        const email = session?.user.email;
        if (!email) throw Error(t("请重新登录后再注销", "Please sign in again before deleting your account."));
        await auth.signInWithPassword({ email, password });
        const mfa = await auth.listMfa();
        const factor = mfa.factors.find(item => item.status === "verified");
        if (factor) {
          setDeletionFactor(factor.id);
          return;
        }
      }
      await api.request("/me", { method: "DELETE" });
      await logout();
    } catch (e) {
      setError(localizeError(e, locale));
    } finally {
      setBusy(false);
      setPassword("");
      setDeletionCode("");
    }
  }
  return (
    <section className="settings-page">
      <div className="page-intro">
        {onClose && <button className="quiet" onClick={onClose}>{t("返回聊天", "Back to chat")}</button>}
        <span className="eyebrow">{t("你的空间，由你决定", "Your space, your choices")}</span>
        <h1>{t("照顾自己，从这里开始", "Settings")}</h1>
      </div>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      <section className="settings-card">
        <h2>{t("账号信息", "Account details")}</h2>
        <p className="account-username">{t("用户名", "Username")}: {profile.email || profile.id}</p>
        <p className="account-user-id">User ID: {profile.id}</p>
      </section>
      <LanguageSettings />
      <section className="settings-card">
        <h2>{t("今日使用情况", "Today’s usage")}</h2>
        {usage ? (
          <div className="usage-grid">
            <div>
              <small>{t("文字对话", "Text conversations")}</small>
              <strong>
                {number.format(usage.text_turns_used)}
                <span> / {number.format(usage.text_turns_limit)} {t("轮", "turns")}</span>
              </strong>
              <progress
                value={usage.text_turns_used}
                max={usage.text_turns_limit || 1}
              />
            </div>
            <div>
              <small>{t("语音陪伴", "Voice conversations")}</small>
              <strong>
                {number.format(Math.floor(usage.voice_seconds_used / 60))}
                <span>
                  {" "}
                  / {number.format(Math.floor(usage.voice_seconds_limit / 60))} {t("分钟", "minutes")}
                </span>
              </strong>
              <progress
                value={usage.voice_seconds_used}
                max={usage.voice_seconds_limit || 1}
              />
            </div>
          </div>
        ) : (
          <p className="muted">{t("用量暂不可用", "Usage is currently unavailable.")}</p>
        )}
      </section>
      <section className="settings-card">
        <h2>{t("你的数据", "Your data")}</h2>
        <p className="muted">
          {t("导出自己的资料与对话记录，或在左侧历史中删除某段对话。", "Export your profile and conversation history, or delete a conversation from the sidebar.")}
        </p>
        <button
          className="secondary"
          disabled={
            busy || (job && !["completed", "failed"].includes(job.status))
          }
          onClick={() => void exportData()}
        >
          {t("导出我的数据", "Export my data")}
        </button>
        {job && (
          <div className="notice" role="status">
            {t("导出状态：", "Export status: ")}
            {{
              queued: t("等待处理", "Queued"),
              running: t("正在整理", "Preparing"),
              completed: t("已完成", "Completed"),
              failed: t("失败", "Failed"),
            }[job.status as string] || job.status}
            {job.status === "completed" && job.result?.asset_id && (
              <button className="secondary" disabled={busy} onClick={() => void downloadData()}>
                {t("下载数据", "Download data")}
              </button>
            )}
            {job.status === "failed" && (
              <p>{t("暂时未能生成导出文件，请稍后重新提交。", "The export could not be created. Please try again later.")}</p>
            )}
          </div>
        )}
      </section>
      <Consents api={api} onHistoryChange={onHistoryChange} />
      {profile.roles?.includes("admin") && <Mfa />}
      <section className="settings-card danger-zone">
        <h2>{t("账户管理", "Account")}</h2>
        <div className="account-actions">
          <button className="secondary" onClick={() => void logout()}>
            {t("退出登录", "Sign out")}
          </button>
          <button
            className="danger"
            onClick={() => {
              setConfirmDelete(!confirmDelete);
              setDeletionFactor("");
              setDeletionCode("");
              setPassword("");
            }}
          >
            {t("注销账户", "Delete account")}
          </button>
        </div>
        {confirmDelete && (
          <form onSubmit={deleteAccount}>
            <p>
              {t("注销后将无法再访问你的空间，资料和对话会进入清理流程。此操作无法撤销。", "Deleting your account ends access to your space and schedules your profile and conversations for deletion. This cannot be undone.")}
            </p>
            {deletionFactor ? (
              <label>
                {t("注销确认动态验证码", "Verification code to confirm account deletion")}
                <input
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={deletionCode}
                  onChange={(e) => setDeletionCode(e.target.value)}
                  placeholder={t("身份验证器中的 6 位数字", "6 digits from your authenticator")}
                />
              </label>
            ) : <label>
              {t("输入登录密码以确认", "Enter your password to confirm")}
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>}
            <button className="danger" disabled={busy}>
              {deletionFactor ? t("验证并注销账户", "Verify and delete account") : t("确认注销我的账户", "Confirm account deletion")}
            </button>
            <button
              type="button"
              className="quiet"
              onClick={() => {
                setConfirmDelete(false);
                setPassword("");
                setDeletionFactor("");
                setDeletionCode("");
              }}
            >
              {t("取消", "Cancel")}
            </button>
          </form>
        )}
      </section>
    </section>
  );
}
